import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Heading, Subheading } from '../catalyst/heading';
import {
	DescriptionList,
	DescriptionTerm,
	DescriptionDetails,
} from '../catalyst/description-list';
import {
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableHeader,
	TableCell,
} from '../catalyst/table';
import { Divider } from '../catalyst/divider';
import { Link } from '../catalyst/link';
import { Text } from '../catalyst/text';
import { Badge } from '../catalyst/badge';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../catalyst/dialog';
import { Button } from '../catalyst/button';
import { Input } from '../catalyst/input';
import { Select } from '../catalyst/select';
import { Field, Label } from '../catalyst/fieldset';
import { Switch } from '../catalyst/switch';
import { updateCode } from '../lib/api';
import type { CodeUpdatePayload } from '../lib/api';
import StatCard from '../components/StatCard';
import ResourceLink from '../components/ResourceLink';
import { CodeStatusBadge } from '../components/StatusBadge';
import { useCode, useCodeRedemptions, useLedgerTransactions } from '../hooks/useCodes';
import { useSupabaseQuery, useSupabaseRow } from '../hooks/useSupabaseQuery';
import { ArrowLeft, Users, ShoppingCart, Hash, Pencil } from 'lucide-react';
import type { User, CodeRedemption } from '../types/database';

function formatBenefit(type: string, currency: string | null, value: number | null): string {
	if (type === 'none' || !value) return 'None';
	if (type === 'percent') return `${value}%${currency ? ` ${currency}` : ''}`;
	if (type === 'fixed_amount' && currency === 'product') return `${value} free product${value > 1 ? 's' : ''}`;
	if (type === 'fixed_amount') return `€${(value / 100).toFixed(2)}${currency ? ` ${currency}` : ''}`;
	return `${value} ${currency || ''}`;
}

function formatDate(dateStr: string): string {
	return new Date(dateStr).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
}

function formatEntryType(entryType: string): string {
	return entryType
		.toLowerCase()
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (char) => char.toUpperCase());
}

export default function CodeDetailPage() {
	const { id } = useParams<{ id: string }>();
	const { data: code, loading: codeLoading, error: codeError, refetch: refetchCode } = useCode(id);
	const { data: owner } = useSupabaseRow<User>('users', code?.owner_id ?? undefined);
	const { data: redemptions, loading: redemptionsLoading } = useCodeRedemptions(id);
	const { data: transactions, loading: transactionsLoading } = useLedgerTransactions({ codeId: id });

	const [editOpen, setEditOpen] = useState(false);
	const [editForm, setEditForm] = useState<Record<string, any>>({});
	const [editSubmitting, setEditSubmitting] = useState(false);
	const [editError, setEditError] = useState<string | null>(null);

	const openEditDialog = () => {
		if (!code) return;
		setEditForm({
			status: code.status,
			starts_at: code.starts_at ? code.starts_at.slice(0, 16) : '',
			ends_at: code.ends_at ? code.ends_at.slice(0, 16) : '',
			order_max_total_uses: code.order_max_total_uses,
			order_max_uses_per_user: code.order_max_uses_per_user,
			order_max_unique_users: code.order_max_unique_users,
			owner_max_total_payout_uses: code.owner_max_total_payout_uses,
			owner_max_payout_uses_per_user: code.owner_max_payout_uses_per_user,
			owner_max_unique_users: code.owner_max_unique_users,
			new_users_only: code.new_users_only,
			redeemer_type: code.redeemer_type,
			redeemer_currency: code.redeemer_currency || '',
			redeemer_value: code.redeemer_value,
			owner_type: code.owner_type,
			owner_currency: code.owner_currency || '',
			owner_value: code.owner_value,
		});
		setEditError(null);
		setEditOpen(true);
	};

	const handleEditSubmit = async () => {
		setEditSubmitting(true);
		setEditError(null);
		try {
			const payload: CodeUpdatePayload = {
				status: editForm.status,
				starts_at: editForm.starts_at || null,
				ends_at: editForm.ends_at || null,
				order_max_total_uses: editForm.order_max_total_uses,
				order_max_uses_per_user: editForm.order_max_uses_per_user,
				order_max_unique_users: editForm.order_max_unique_users,
				owner_max_total_payout_uses: editForm.owner_max_total_payout_uses,
				owner_max_payout_uses_per_user: editForm.owner_max_payout_uses_per_user,
				owner_max_unique_users: editForm.owner_max_unique_users,
				new_users_only: editForm.new_users_only,
				redeemer_type: editForm.redeemer_type,
				redeemer_currency: editForm.redeemer_type !== 'none' ? (editForm.redeemer_currency || null) : null,
				redeemer_value: editForm.redeemer_type !== 'none' ? editForm.redeemer_value : null,
				owner_type: editForm.owner_type,
				owner_currency: editForm.owner_type !== 'none' ? (editForm.owner_currency || null) : null,
				owner_value: editForm.owner_type !== 'none' ? editForm.owner_value : null,
			};
			await updateCode(code!.id, payload);
			setEditOpen(false);
			refetchCode();
		} catch (err: any) {
			setEditError(err.message || 'Failed to update code');
		} finally {
			setEditSubmitting(false);
		}
	};


	// Usage stats: derived from redemptions
	const { data: allRedemptions } = useSupabaseQuery<CodeRedemption>(
		(client) =>
			client
				.from('code_redemptions')
				.select('user_id, order_id')
				.eq('code_id', id ?? ''),
		[id]
	);

	const totalRedemptions = allRedemptions.length;
	const uniqueUsers = new Set(allRedemptions.map((r) => r.user_id)).size;
	const ordersGenerated = allRedemptions.filter((r) => r.order_id != null).length;

	if (codeLoading) {
		return <div className="py-12 text-center text-zinc-500">Loading code...</div>;
	}

	if (codeError) {
		return (
			<div className="py-12 text-center">
				<p className="text-red-600 dark:text-red-400 mb-4">{codeError}</p>
				<Link href="/codes" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300">
					<ArrowLeft className="h-4 w-4" />
					Back to Codes
				</Link>
			</div>
		);
	}

	if (!code) {
		return (
			<div className="py-12 text-center">
				<p className="text-zinc-500 mb-4">Code not found.</p>
				<Link href="/codes" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300">
					<ArrowLeft className="h-4 w-4" />
					Back to Codes
				</Link>
			</div>
		);
	}

	return (
		<div>
			{/* Header */}
			<div className="mb-6">
				<Link href="/codes" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 mb-4">
					<ArrowLeft className="h-4 w-4" />
					Back to Codes
				</Link>
				<div className="flex flex-wrap items-center gap-4">
					<Heading>{code.code}</Heading>
					<CodeStatusBadge status={code.status} />
					<Button outline onClick={openEditDialog}>
						<Pencil className="h-4 w-4" />
						Edit
					</Button>
				</div>
			</div>

			<Divider className="my-6" />

			{/* Code Configuration */}
			<section className="mb-6">
				<Subheading className="mb-4">Code Configuration</Subheading>
				<DescriptionList>
					<DescriptionTerm>Code</DescriptionTerm>
					<DescriptionDetails>
						<code className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs font-mono dark:bg-zinc-800">
							{code.code}
						</code>
					</DescriptionDetails>

					<DescriptionTerm>Status</DescriptionTerm>
					<DescriptionDetails><CodeStatusBadge status={code.status} /></DescriptionDetails>

					<DescriptionTerm>Owner</DescriptionTerm>
					<DescriptionDetails>
						{code.owner_id ? (
							owner ? (
								<ResourceLink
									id={code.owner_id}
									href={`/users/${code.owner_id}`}
									name={owner.display_name || owner.phone_number}
								/>
							) : (
								<ResourceLink id={code.owner_id} href={`/users/${code.owner_id}`} />
							)
						) : (
							'—'
						)}
					</DescriptionDetails>

					<DescriptionTerm>Date Range</DescriptionTerm>
					<DescriptionDetails>
						{code.starts_at || code.ends_at ? (
							<>
								{code.starts_at ? formatDate(code.starts_at) : '∞'}
								{' — '}
								{code.ends_at ? formatDate(code.ends_at) : '∞'}
							</>
						) : (
							'No restriction'
						)}
					</DescriptionDetails>

					<DescriptionTerm>Order Max Total Uses</DescriptionTerm>
					<DescriptionDetails>{code.order_max_total_uses ?? '—'}</DescriptionDetails>

					<DescriptionTerm>Order Max Per User</DescriptionTerm>
					<DescriptionDetails>{code.order_max_uses_per_user ?? '—'}</DescriptionDetails>

					<DescriptionTerm>Order Max Unique Users</DescriptionTerm>
					<DescriptionDetails>{code.order_max_unique_users ?? '—'}</DescriptionDetails>

					<DescriptionTerm>Owner Max Total Payout Uses</DescriptionTerm>
					<DescriptionDetails>{code.owner_max_total_payout_uses ?? '—'}</DescriptionDetails>

					<DescriptionTerm>Owner Max Payouts Per User</DescriptionTerm>
					<DescriptionDetails>{code.owner_max_payout_uses_per_user ?? '—'}</DescriptionDetails>

					<DescriptionTerm>Owner Max Unique Users</DescriptionTerm>
					<DescriptionDetails>{code.owner_max_unique_users ?? '—'}</DescriptionDetails>

					<DescriptionTerm>New Users Only</DescriptionTerm>
					<DescriptionDetails>{code.new_users_only ? 'Yes' : 'No'}</DescriptionDetails>

					<DescriptionTerm>Redeemer Benefit</DescriptionTerm>
					<DescriptionDetails>{formatBenefit(code.redeemer_type, code.redeemer_currency, code.redeemer_value)}</DescriptionDetails>

					<DescriptionTerm>Owner Benefit</DescriptionTerm>
					<DescriptionDetails>{formatBenefit(code.owner_type, code.owner_currency, code.owner_value)}</DescriptionDetails>

					<DescriptionTerm>Created</DescriptionTerm>
					<DescriptionDetails>{formatDate(code.created_at)}</DescriptionDetails>
				</DescriptionList>
			</section>

			<Divider className="my-6" />

			{/* Usage Stats */}
			<section className="mb-6">
				<Subheading className="mb-4">Usage Stats</Subheading>
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
					<StatCard
						title="Total Redemptions"
						value={totalRedemptions}
						icon={<Hash className="size-5" />}
					/>
					<StatCard
						title="Unique Users"
						value={uniqueUsers}
						icon={<Users className="size-5" />}
					/>
					<StatCard
						title="Orders Generated"
						value={ordersGenerated}
						icon={<ShoppingCart className="size-5" />}
					/>
				</div>
			</section>

			<Divider className="my-6" />

			{/* Redemption History */}
			<section className="mb-6">
				<Subheading className="mb-4">Redemption History</Subheading>
				{redemptionsLoading ? (
					<Text className="text-sm text-zinc-500">Loading redemptions...</Text>
				) : redemptions.length === 0 ? (
					<Text className="text-sm text-zinc-500">No redemptions recorded.</Text>
				) : (
					<Table dense>
						<TableHead>
							<TableRow>
								<TableHeader>User</TableHeader>
								<TableHeader>Order</TableHeader>
								<TableHeader>Apply Count</TableHeader>
								<TableHeader>First Applied</TableHeader>
								<TableHeader>Last Applied</TableHeader>
								<TableHeader>Created</TableHeader>
							</TableRow>
						</TableHead>
						<TableBody>
							{redemptions.map((r) => (
								<TableRow key={r.id}>
									<TableCell>
										<ResourceLink
											id={r.user_id}
											href={`/users/${r.user_id}`}
											name={r.users?.display_name || r.users?.phone_number}
										/>
									</TableCell>
									<TableCell>
										{r.order_id ? (
											<ResourceLink
												id={r.order_id}
												href={`/orders/${r.order_id}`}
											/>
										) : (
											'—'
										)}
									</TableCell>
									<TableCell>
										{r.apply_count}
									</TableCell>
									<TableCell className="text-zinc-500">{formatDate(r.first_applied_at)}</TableCell>
									<TableCell className="text-zinc-500">{formatDate(r.last_applied_at)}</TableCell>
									<TableCell className="text-zinc-500">{formatDate(r.created_at)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</section>

			<Divider className="my-6" />

			{/* Related Ledger Transactions */}
			<section className="mb-6">
				<Subheading className="mb-4">Related Ledger Transactions</Subheading>
				{transactionsLoading ? (
					<Text className="text-sm text-zinc-500">Loading transactions...</Text>
				) : transactions.length === 0 ? (
					<Text className="text-sm text-zinc-500">No ledger transactions recorded.</Text>
				) : (
					<Table dense>
						<TableHead>
							<TableRow>
								<TableHeader>User</TableHeader>
								<TableHeader>Order</TableHeader>
								<TableHeader>Type</TableHeader>
								<TableHeader>Currency</TableHeader>
								<TableHeader>Amount</TableHeader>
								<TableHeader>Reason</TableHeader>
								<TableHeader>Created</TableHeader>
							</TableRow>
						</TableHead>
						<TableBody>
							{transactions.map((t) => (
								<TableRow key={t.id}>
									<TableCell>
										<ResourceLink
											id={t.user_id}
											href={`/users/${t.user_id}`}
											name={t.users?.display_name || t.users?.phone_number}
										/>
									</TableCell>
									<TableCell>
										{t.order_id ? (
											<ResourceLink
												id={t.order_id}
												href={`/orders/${t.order_id}`}
											/>
										) : (
											'—'
										)}
									</TableCell>
									<TableCell>
										<Badge color="zinc">{formatEntryType(t.entry_type)}</Badge>
									</TableCell>
									<TableCell className="uppercase">{t.currency}</TableCell>
									<TableCell className={t.amount < 0 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-green-600 dark:text-green-400 font-medium'}>
											{t.amount < 0 ? '' : '+'}€{(Math.abs(t.amount) / 100).toFixed(2)}
									</TableCell>
									<TableCell className="max-w-[200px] truncate text-zinc-500">
										{t.reason || '—'}
									</TableCell>
									<TableCell className="text-zinc-500">{formatDate(t.created_at)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</section>

			{/* Edit Code Dialog */}
			<Dialog open={editOpen} onClose={() => setEditOpen(false)} size="2xl">
				<DialogTitle>Edit Code</DialogTitle>
				<DialogBody>
					{editError && (
						<div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
							{editError}
						</div>
					)}
					<div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
						<Field>
							<Label>Status</Label>
							<Select
								value={editForm.status || 'active'}
								onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setEditForm({ ...editForm, status: e.target.value })}
							>
								<option value="active">Active</option>
								<option value="paused">Paused</option>
								<option value="expired">Expired</option>
							</Select>
						</Field>
						<Field>
							<Label>Starts At</Label>
							<Input
								type="datetime-local"
								value={editForm.starts_at || ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditForm({ ...editForm, starts_at: e.target.value })}
							/>
						</Field>
						<Field>
							<Label>Ends At</Label>
							<Input
								type="datetime-local"
								value={editForm.ends_at || ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditForm({ ...editForm, ends_at: e.target.value })}
							/>
						</Field>
						<Field>
							<Label>Order Max Total Uses</Label>
							<Input
								type="number"
								value={editForm.order_max_total_uses ?? ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditForm({ ...editForm, order_max_total_uses: e.target.value ? Number(e.target.value) : null })}
							/>
						</Field>
						<Field>
							<Label>Order Max Uses per User</Label>
							<Input
								type="number"
								value={editForm.order_max_uses_per_user ?? ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditForm({ ...editForm, order_max_uses_per_user: e.target.value ? Number(e.target.value) : null })}
							/>
						</Field>
						<Field>
							<Label>Order Max Unique Users</Label>
							<Input
								type="number"
								value={editForm.order_max_unique_users ?? ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditForm({ ...editForm, order_max_unique_users: e.target.value ? Number(e.target.value) : null })}
							/>
						</Field>
						<Field>
							<Label>Owner Max Total Payout Uses</Label>
							<Input
								type="number"
								value={editForm.owner_max_total_payout_uses ?? ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditForm({ ...editForm, owner_max_total_payout_uses: e.target.value ? Number(e.target.value) : null })}
							/>
						</Field>
						<Field>
							<Label>Owner Max Payouts Per User</Label>
							<Input
								type="number"
								value={editForm.owner_max_payout_uses_per_user ?? ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditForm({ ...editForm, owner_max_payout_uses_per_user: e.target.value ? Number(e.target.value) : null })}
							/>
						</Field>
						<Field>
							<Label>Owner Max Unique Users</Label>
							<Input
								type="number"
								value={editForm.owner_max_unique_users ?? ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditForm({ ...editForm, owner_max_unique_users: e.target.value ? Number(e.target.value) : null })}
							/>
						</Field>
						<Field className="flex items-center gap-3 pt-6">
							<Switch
								checked={editForm.new_users_only ?? false}
								onChange={(checked: boolean) => setEditForm({ ...editForm, new_users_only: checked })}
								color="blue"
							/>
							<Label>New Users Only</Label>
						</Field>

						{/* Redeemer Benefit */}
						<div className="sm:col-span-2">
							<Text className="mb-3 font-medium text-zinc-900 dark:text-white">Redeemer Benefit</Text>
							<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
								<Field>
									<Label>Type</Label>
									<Select
										value={editForm.redeemer_type || 'none'}
										onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setEditForm({ ...editForm, redeemer_type: e.target.value })}
									>
										<option value="none">None</option>
										<option value="percent">Percent</option>
										<option value="fixed_amount">Fixed Amount</option>
									</Select>
								</Field>
								<Field>
									<Label>Currency</Label>
									<Select
										value={editForm.redeemer_currency || ''}
										onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setEditForm({ ...editForm, redeemer_currency: e.target.value || null })}
										disabled={editForm.redeemer_type === 'none'}
									>
										<option value="">—</option>
										<option value="credit">Credit</option>
										<option value="cash">Cash</option>
										<option value="product">Product</option>
									</Select>
								</Field>
								<Field>
									<Label>Value</Label>
									<Input
										type="number"
										value={editForm.redeemer_value ?? ''}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditForm({ ...editForm, redeemer_value: e.target.value ? Number(e.target.value) : null })}
										disabled={editForm.redeemer_type === 'none'}
									/>
								</Field>
							</div>
						</div>

						{/* Owner Benefit */}
						<div className="sm:col-span-2">
							<Text className="mb-3 font-medium text-zinc-900 dark:text-white">Owner Benefit</Text>
							<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
								<Field>
									<Label>Type</Label>
									<Select
										value={editForm.owner_type || 'none'}
										onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setEditForm({ ...editForm, owner_type: e.target.value })}
									>
										<option value="none">None</option>
										<option value="percent">Percent</option>
										<option value="fixed_amount">Fixed Amount</option>
									</Select>
								</Field>
								<Field>
									<Label>Currency</Label>
									<Select
										value={editForm.owner_currency || ''}
										onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setEditForm({ ...editForm, owner_currency: e.target.value || null })}
										disabled={editForm.owner_type === 'none'}
									>
										<option value="">—</option>
										<option value="credit">Credit</option>
										<option value="cash">Cash</option>
										<option value="product">Product</option>
									</Select>
								</Field>
								<Field>
									<Label>Value</Label>
									<Input
										type="number"
										value={editForm.owner_value ?? ''}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditForm({ ...editForm, owner_value: e.target.value ? Number(e.target.value) : null })}
										disabled={editForm.owner_type === 'none'}
									/>
								</Field>
							</div>
						</div>
					</div>
				</DialogBody>
				<DialogActions>
					<Button plain onClick={() => setEditOpen(false)}>Cancel</Button>
					<Button onClick={handleEditSubmit} disabled={editSubmitting}>
						{editSubmitting ? 'Saving...' : 'Save Changes'}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
}
